import type { SessionIdentity } from "@pustak-lms/contracts";
import { ApiRequestError, notifyApiMutation } from "./index";

// One same-origin transport for the Worker-owned Better Auth and application APIs.
export function createAuthClient() {
  async function request<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(path, {
      method: body === undefined ? "GET" : "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new ApiRequestError(response.status, payload ?? undefined);
    if (!payload || typeof payload !== "object" || (body === undefined ? !("data" in payload) : payload.success !== true)) {
      throw new Error("The authentication service returned an invalid response. Please try again.");
    }
    return payload as T;
  }
  return {
    getSession: async () => (await request<{ data: SessionIdentity | null }>("/api/session")).data,
    signIn: (email: string, password: string) => request<{ success: true }>("/api/auth/sign-in/email", { email, password }),
    signOut: () => request("/api/auth/sign-out", {}),
    updateProfile: async (name: string) => {
      const result = await request<{ success: true }>("/api/auth/update-user", { name });
      notifyApiMutation();
      return result;
    },
    forgotPassword: (email: string, surface: "platform" | "manage" | "learn") => request("/api/auth/request-password-reset", { email, surface }),
    resetPassword: (token: string, newPassword: string, confirmPassword: string) => request("/api/auth/reset-password", { token, newPassword, confirmPassword }),
    changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => request("/api/auth/change-password", { currentPassword, newPassword, confirmPassword })
  };
}
export type AuthClient = ReturnType<typeof createAuthClient>;
